import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import { FaArrowLeft, FaDownload, FaShareAlt } from 'react-icons/fa';
import html2pdf from 'html2pdf.js';
import './Page.css';
import MaturityScorecard from '../reports/MaturityScorecard';

const MaturityScorecardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        
        // If ID is provided, fetch that specific assessment
        if (id) {
          const res = await axios.get(`/api/assessments/${id}`);
          
          // Process the assessment data to ensure we have category scores
          const processedAssessment = processAssessmentData(res.data);
          setAssessment(processedAssessment);
        } else {
          // Otherwise, get the most recent completed assessment
          const assessmentsRes = await axios.get('/api/assessments');
          const completedAssessments = assessmentsRes.data.filter(a => a.status === 'completed');
          
          if (completedAssessments.length > 0) {
            // Sort by date (newest first) and get the first one
            const sortedAssessments = completedAssessments.sort(
              (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
            );
            
            // Process the assessment data to ensure we have category scores
            const processedAssessment = processAssessmentData(sortedAssessments[0]);
            setAssessment(processedAssessment);
          } else {
            setError(t('reports.noCompletedAssessments'));
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching assessment for scorecard:', err);
        setError(t('reports.errorFetchingData'));
        setLoading(false);
      }
    };

    fetchAssessment();
  }, [id, t]);

  const handleDownloadPDF = async () => {
    try {
      setLoading(true);
      
      // Get the content to convert to PDF
      const element = document.getElementById('scorecard-content');
      
      if (!element) {
        throw new Error('Content element not found');
      }
      
      // Create a custom PDF with jsPDF directly
      const generatePDF = async () => {
        // First use html2pdf to convert HTML to canvas
        const opt = {
          margin: [0.5, 0.5, 1.5, 0.5], // Increased bottom margin for footer
          filename: `Maturity_Scorecard_${assessment?.title ? assessment.title.replace(/\s+/g, '_') : 'Report'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'cm', format: 'a4', orientation: 'landscape' }, // Changed to landscape
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: ['tr', 'td', 'th', 'img', 'div.scorecard-section'] }
        };
        
        // Add custom footer to each page
        const footerText = `\u00a9 ${new Date().getFullYear()} Business Resilience Navigator. All rights reserved.`;
        
        const worker = html2pdf().from(element).set(opt);
        
        // Add footer to each page
        worker.toPdf().get('pdf').then((pdf) => {
          // Get total page count
          const totalPages = pdf.internal.getNumberOfPages();
          
          // Add footer to each page
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            
            // Add page number
            pdf.setFontSize(10);
            pdf.setTextColor(76, 84, 84); // #4C5454
            
            // Add page number first
            pdf.text(
              `Page ${i} of ${totalPages}`,
              pdf.internal.pageSize.width / 2,
              pdf.internal.pageSize.height - 1.2,
              { align: 'center' }
            );
            
            // Add copyright
            pdf.text(
              footerText,
              pdf.internal.pageSize.width / 2,
              pdf.internal.pageSize.height - 0.8,
              { align: 'center' }
            );
            
            // Add line below the text
            pdf.setDrawColor(210, 186, 123); // #D2BA7B
            pdf.setLineWidth(0.02); // Make line slightly thicker
            pdf.line(
              0.5, 
              pdf.internal.pageSize.height - 0.6, 
              pdf.internal.pageSize.width - 0.5, 
              pdf.internal.pageSize.height - 0.6
            );
          }
          
          // Save the PDF
          pdf.save(`Maturity_Scorecard_${assessment?.title ? assessment.title.replace(/\s+/g, '_') : 'Report'}.pdf`);
          setLoading(false);
        }).catch(err => {
          console.error('Error generating PDF:', err);
          setError(t('reports.errorDownloadingPDF') + ': ' + (err.message || 'Unknown error'));
          setLoading(false);
          alert('Failed to generate PDF. Please try again later.');
        });
      };
      
      generatePDF();
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError(t('reports.errorDownloadingPDF') + ': ' + (err.message || 'Unknown error'));
      setLoading(false);
      alert('Failed to generate PDF. Please try again later.');
    }
  };

  const handleShare = () => {
    // Implement share functionality
    alert('Share functionality will be implemented in a future update.');
  };

  // Process assessment data to ensure we have category scores
  const processAssessmentData = (assessmentData) => {
    if (!assessmentData) return null;
    
    // If we already have category scores, return as is
    if (assessmentData.categoryScores && Object.keys(assessmentData.categoryScores).length > 0) {
      return assessmentData;
    }
    
    // Otherwise, calculate category scores from responses
    const responses = assessmentData.responses || [];
    const answeredResponses = responses.filter(r => r.answer && r.answer !== '');
    
    // Get unique categories
    const categories = [...new Set(responses.map(r => r.category))];
    
    // Calculate score for each category
    const categoryScores = {};
    categories.forEach(category => {
      const categoryResponses = answeredResponses.filter(r => r.category === category);
      
      if (categoryResponses.length > 0) {
        const totalScore = categoryResponses.reduce((sum, r) => sum + parseInt(r.answer || 0), 0);
        const maxPossibleScore = categoryResponses.length * 5; // Assuming 5 is the max score per question
        categoryScores[category] = totalScore / maxPossibleScore;
      }
    });
    
    // Calculate overall score if not present
    let score = assessmentData.score;
    if (!score && answeredResponses.length > 0) {
      const totalScore = answeredResponses.reduce((sum, r) => sum + parseInt(r.answer || 0), 0);
      const maxPossibleScore = answeredResponses.length * 5; // Assuming 5 is the max score per question
      score = Math.round((totalScore / maxPossibleScore) * 100);
    }
    
    // Return enhanced assessment data
    return {
      ...assessmentData,
      categoryScores: categoryScores,
      score: score || 0
    };
  };

  if (loading && !assessment) {
    return (
      <div className="page-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title-section">
          <button className="back-button" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back to Assessment
          </button>
          <h1 className="page-title">{t('reports.maturityScorecard')}</h1>
        </div>
        <div className="page-actions">
          <button className="action-button" onClick={handleDownloadPDF}>
            <FaDownload /> {t('reports.downloadPDF')}
          </button>
          <button className="action-button" onClick={handleShare}>
            <FaShareAlt /> {t('reports.share')}
          </button>
        </div>
      </div>
      
      <div id="scorecard-content" style={{ maxWidth: '100%', pageBreakInside: 'avoid' }}>
        <style>
          {`
            @media print {
              .scorecard-container {
                width: 100%;
                max-width: 100%;
                page-break-inside: avoid;
              }
              .scorecard-section {
                page-break-inside: avoid;
              }
              .scorecard-table {
                page-break-inside: avoid;
                width: 100%;
              }
              .scorecard-row {
                page-break-inside: avoid;
              }
              .legend-container {
                page-break-before: avoid;
              }
            }
            
            /* Hide cover page */
            .cover-page-container {
              display: none !important;
            }
            
            /* Hide table of contents */
            .table-of-contents {
              display: none !important;
            }
            
            /* Hide questions and answers section */
            .questions-answers-section {
              display: none !important;
            }
            
            /* Make sure the scorecard table is visible */
            .scorecard-container {
              display: block !important;
            }
          `}
        </style>
        {error ? (
          <div className="error-message">
            <p>{error}</p>
            <button 
              className="primary-button"
              onClick={() => navigate('/assessments/new')}
            >
              {t('assessments.startNewAssessment')}
            </button>
          </div>
        ) : (
          <>
            <MaturityScorecard assessmentData={{
              ...assessment,
              organization: user?.organizationName || t('reports.yourOrganization'),
              industry: assessment?.industryType || user?.industryType || t('reports.notSpecified'),
              title: assessment?.title || t('reports.maturityAssessment')
            }} />

            <div className="scorecard-description">
              <h3>{t('reports.howToReadScorecard')}</h3>
              <p>{t('reports.scorecardDescription')}</p>
              <ul>
                <li><strong>{t('reports.none')}:</strong> {t('reports.noneDescription')}</li>
                <li><strong>{t('reports.initial')}:</strong> {t('reports.initialDescription')}</li>
                <li><strong>{t('reports.defined')}:</strong> {t('reports.definedDescription')}</li>
                <li><strong>{t('reports.managed')}:</strong> {t('reports.managedDescription')}</li>
                <li><strong>{t('reports.compliant')}:</strong> {t('reports.compliantDescription')}</li>
                <li><strong>{t('reports.optimized')}:</strong> {t('reports.optimizedDescription')}</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MaturityScorecardPage;
